import { useState } from 'react'
import type { SchainNode, ServiceConfig } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field } from '../components/fields'
import { JsonInline } from '../components/json'
import { getDeep, setDeep } from '../lib/deep'

// Adaptery obecne w bundlu prebid.js (patrz prebid-build.modules.json w swnext).
// Dostawca spoza tej listy wymaga przebudowania bundla, inaczej pbjs go zignoruje.
const KNOWN_BIDDERS = ['teads', 'gopl', 'visx', 'rtbhouse', 'easybid', 'adquery']

const schainInput =
	'w-full rounded border border-slate-200 bg-white px-2 py-1 font-mono text-xs outline-none focus:border-slate-400'

export function BiddersEditor({
  draft,
  setDraft,
}: {
  draft: ServiceConfig
  setDraft: (cfg: ServiceConfig) => void
}) {
  const [newBidder, setNewBidder] = useState('')
  // Formularz schain pokazujemy dopiero po zaznaczeniu checkboxa. Trzyma też to, co
  // wpisane, ale jeszcze niekompletne — do `draft` idzie wyłącznie pełny węzeł,
  // bo takiego wymaga schemat (asi + sid) i sam pbjs.
  const [schainOpen, setSchainOpen] = useState<Record<string, { asi: string; sid: string }>>(() =>
    Object.fromEntries(
      Object.entries(draft.schain ?? {}).map(([k, n]) => [k, { asi: n.asi, sid: n.sid }]),
    ),
  )
  const bidders = draft.bidders ?? {}
  const schain = draft.schain ?? {}
  const names = Object.keys(bidders)
  const blueprints = draft.slotBlueprints ?? []

  const iframeBidders: string[] = (() => {
    const v = getDeep(draft.prebid, 'userSync', 'filterSettings', 'iframe', 'bidders')
    return Array.isArray(v) ? (v as string[]) : []
  })()

  const iframeFilter = (() => {
    const v = getDeep(draft.prebid, 'userSync', 'filterSettings', 'iframe', 'filter')
    return typeof v === 'string' ? v : 'include'
  })()

  function setBidders(next: Record<string, Record<string, unknown>>) {
    setDraft({ ...draft, bidders: next })
  }

  function dropSchain(name: string) {
    const rest = { ...schain }
    delete rest[name]
    return rest
  }

  function toggleSchain(name: string, on: boolean) {
    setSchainOpen((prev) => {
      const next = { ...prev }
      if (on) next[name] = next[name] ?? { asi: '', sid: '' }
      else delete next[name]
      return next
    })
    if (!on) setDraft({ ...draft, schain: dropSchain(name) })
  }

  // Węzeł schain dostawcy. Niekompletny (brak asi lub sid) nie trafia do configu —
  // pbjs odrzuca taki węzeł w całości i schain nie poleci wcale.
  function setSchainNode(name: string, patch: Partial<SchainNode>) {
    const node = { ...(schainOpen[name] ?? { asi: '', sid: '' }), ...patch }
    setSchainOpen((prev) => ({ ...prev, [name]: node }))
    const rest = dropSchain(name)
    if (node.asi.trim() && node.sid.trim()) {
      rest[name] = { asi: node.asi.trim(), sid: node.sid.trim(), hp: 1 }
    }
    setDraft({ ...draft, schain: rest })
  }

  function setIframeBidders(next: string[]) {
    setDraft({
      ...draft,
      prebid: setDeep(draft.prebid, ['userSync', 'filterSettings', 'iframe', 'bidders'], next),
    })
  }

  function setIframeFilter(filter: string) {
    setDraft({
      ...draft,
      prebid: setDeep(draft.prebid, ['userSync', 'filterSettings', 'iframe', 'filter'], filter),
    })
  }

  function addBidder(name: string) {
    const trimmed = name.trim()
    if (!trimmed || trimmed in bidders) return
    setBidders({ ...bidders, [trimmed]: {} })
    setNewBidder('')
  }

  function removeBidder(name: string) {
    const usedBy = blueprints
      .filter((bp) => (bp.bids ?? []).some((b) => b.bidder === name))
      .map((bp) => bp.slot)
    if (usedBy.length > 0) {
      const preview = usedBy.slice(0, 3).join(', ') + (usedBy.length > 3 ? '…' : '')
      const ok = window.confirm(
        `„${name}" jest używany na ${usedBy.length} slotach (${preview}).\n\n` +
          'Usunięcie z rejestru zabierze im wspólne parametry — bidy polecą niekompletne. Usunąć?',
      )
      if (!ok) return
    }
    toggleSchain(name, false)
    const { [name]: _omit, ...rest } = bidders
    const { [name]: _omitSchain, ...schainRest } = schain
    setDraft({
      ...draft,
      bidders: rest,
      schain: schainRest,
      prebid: setDeep(
        draft.prebid,
        ['userSync', 'filterSettings', 'iframe', 'bidders'],
        iframeBidders.filter((b) => b !== name),
      ),
    })
  }

  const suggestions = KNOWN_BIDDERS.filter((b) => !(b in bidders))

  // Dostawca używany na slocie, ale bez wpisu w rejestrze — nie dostanie wspólnych params.
  const orphans = [
    ...new Set(
      blueprints.flatMap((bp) =>
        (bp.bids ?? []).map((b) => b.bidder).filter((b) => b && !(b in bidders)),
      ),
    ),
  ]

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 p-4">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Rejestr dostawców ({names.length})
          </h3>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            iframe sync filter:
            <select
              value={iframeFilter}
              onChange={(e) => setIframeFilter(e.target.value)}
              className="rounded border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-slate-400"
            >
              <option value="include">include</option>
              <option value="exclude">exclude</option>
            </select>
          </label>
        </div>
        <p className="mb-4 text-xs text-slate-400">
          Parametry wspólne dla całego serwisu. Slot dopisuje tylko to, co różni się per placement
          (visx <code>uid</code>, teads <code>placementId</code>) — i tym nadpisuje rejestr.
        </p>

        {orphans.length > 0 && (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            Na slotach używani są dostawcy spoza rejestru:{' '}
            <code className="font-semibold">{orphans.join(', ')}</code>. Dodaj ich niżej albo popraw
            nazwę na slocie.
          </div>
        )}

        <div className="mb-4 flex gap-2">
          <input
            list="known-bidders-list"
            value={newBidder}
            onChange={(e) => setNewBidder(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addBidder(newBidder)}
            placeholder="nazwa (np. teads)"
            className="flex-1 rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400"
          />
          <datalist id="known-bidders-list">
            {suggestions.map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
          <Button
            type="button"
            size="sm"
            onClick={() => addBidder(newBidder)}
            disabled={!newBidder.trim() || newBidder.trim() in bidders}
          >
            + Dodaj
          </Button>
        </div>

        {names.length === 0 ? (
          <p className="text-xs text-slate-400">Brak dostawców.</p>
        ) : (
          <ul className="space-y-2">
            {names.map((name) => {
              const slots = blueprints.filter((bp) =>
                (bp.bids ?? []).some((b) => b.bidder === name),
              ).length
              return (
								<li key={name} className="rounded border border-slate-200 bg-white p-3">
									<div className="mb-2 flex flex-wrap items-center gap-3">
										<code className="font-mono text-sm font-semibold text-slate-800">{name}</code>
										<Badge variant="outline">
											{slots} {slots === 1 ? 'slot' : 'slotów'}
										</Badge>
										{!KNOWN_BIDDERS.includes(name) && (
											<Badge
												variant="outline"
												className="border-amber-300 text-amber-700"
												title="Adapter musi być w bundlu prebid.js — patrz prebid-build.modules.json"
											>
												adapter spoza bundla?
											</Badge>
										)}
										<label className="flex items-center gap-2 text-xs text-slate-600">
											<Checkbox
												checked={iframeBidders.includes(name)}
												onCheckedChange={(v) =>
													setIframeBidders(v === true ? [...iframeBidders, name] : iframeBidders.filter((b) => b !== name))
												}
											/>
											iframe user-sync
										</label>
										<label className="flex items-center gap-2 text-xs text-slate-600">
											<Checkbox
												checked={name in schainOpen}
												onCheckedChange={(v) => toggleSchain(name, v === true)}
											/>
											schain
										</label>
										<div className="flex-1" />
										<button
											type="button"
											onClick={() => removeBidder(name)}
											className="rounded border border-rose-200 px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
										>
											Usuń
										</button>
									</div>

									<label className="text-[11px] uppercase tracking-wide text-slate-500">params wspólne (JSON)</label>
									<JsonInline
										value={bidders[name] ?? {}}
										onChange={(p) => setBidders({ ...bidders, [name]: p as Record<string, unknown> })}
									/>
									{name in schainOpen && (
										<div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
											<Field label="schain asi">
												<input
													value={schainOpen[name]?.asi ?? ''}
													onChange={(e) => setSchainNode(name, { asi: e.target.value })}
													placeholder={`domena SSP, np. ${name.toLowerCase()}.com`}
													className={schainInput}
												/>
											</Field>
											<Field label="schain sid (hp=1)">
												<input
													value={schainOpen[name]?.sid ?? ''}
													onChange={(e) => setSchainNode(name, { sid: e.target.value })}
													placeholder="nasze ID w sellers.json SSP"
													className={schainInput}
												/>
											</Field>
										</div>
									)}
								</li>
							);
            })}
          </ul>
        )}

        {suggestions.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="mb-2 text-xs text-slate-400">Znani dostawcy (kliknij, aby dodać):</p>
            <div className="flex flex-wrap gap-1">
              {suggestions.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => addBidder(b)}
                  className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-600 hover:border-slate-300 hover:bg-slate-100"
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
          UserIds (JSON)
        </h3>
        <p className="mb-2 text-xs text-slate-400">prebid.userSync.userIds</p>
        <JsonInline
          value={getDeep(draft.prebid, 'userSync', 'userIds') ?? []}
          onChange={(v) =>
            setDraft({ ...draft, prebid: setDeep(draft.prebid, ['userSync', 'userIds'], v) })
          }
        />
      </section>
    </div>
  )
}
